import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import {
  ChangeEvent,
  CmsModel,
  CmsModelEntry,
  CmsWebsiteConfigBM,
  FileEntryBM,
  jsonCopy,
} from '@bm-js/atlascloud-shared'
import { useBeforeUnload } from '../hooks/useBeforeUnload'
import { _omit } from '@naturalcycles/js-lib/object'
import { useFetchOnMount } from '../hooks/useFetchOnMount'
import { useTr } from '../utils/useTr'
import { TrKey } from '../cnst/translations/trKey.cnst'
import {
  CmsEditorBackendResponse,
  CmsEditorContext,
  CmsEditorState,
} from '../contexts/CmsEditor.context'
import { useCmsEditorApi } from '../hooks/useCmsEditorApi'

export const CmsEditorProvider = ({ children }: PropsWithChildren) => {
  const [workingConfig, setWorkingConfig] = useState<CmsWebsiteConfigBM>()
  const [isSaving, setIsSaving] = useState(false)
  const [isPublishing, setIsPublishing] = useState(false)
  const [activeModelUuid, setActiveModelUuid] = useState<string | null>(null)
  const [activeEntryIndex, setActiveEntryIndex] = useState<number | null>(null)

  const { saveConfig, publishConfig } = useCmsEditorApi()
  const { tr } = useTr()
  const { id } = useParams()

  const onSuccess = useCallback((data: CmsEditorBackendResponse) => {
    setWorkingConfig(jsonCopy(data.websiteConfig))
  }, [])

  const {
    isError,
    isLoading,
    data: backendResponse,
    revalidate,
  } = useFetchOnMount<CmsEditorBackendResponse>(
    `cms-user/website/editor/${id}`,
    {
      onSuccess,
    }
  )

  const isSaved = useMemo(() => {
    if (!workingConfig || !backendResponse) return true
    return (
      JSON.stringify(workingConfig) ===
      JSON.stringify(backendResponse.websiteConfig)
    )
  }, [workingConfig, backendResponse])

  useBeforeUnload(!isSaved, tr(TrKey.editorHaveYouSaved))

  const activeModel = useMemo(() => {
    return (
      backendResponse?.website.cmsModels.find(
        (model) => model.uuid === activeModelUuid
      ) ?? null
    )
  }, [backendResponse, activeModelUuid])

  const activeEntry = useMemo(() => {
    if (!activeModel || !workingConfig || activeEntryIndex === null) return null
    return workingConfig.entities[activeModel.key].entries[activeEntryIndex]
  }, [activeEntryIndex, activeModel, workingConfig])

  const editorData = useMemo<CmsEditorState['editorData']>(() => {
    if (!backendResponse || !workingConfig) return undefined
    return {
      backendResponse: _omit(backendResponse, ['websiteConfig']),
      workingConfig,
      activeModel,
      activeEntry,
    }
  }, [backendResponse, workingConfig, activeModel, activeEntry])

  const saveConfig_ = useCallback(async () => {
    if (!workingConfig) return
    setIsSaving(true)
    await saveConfig(workingConfig)
    await revalidate()
    setIsSaving(false)
  }, [workingConfig, saveConfig, revalidate])

  const publishConfig_ = useCallback(async () => {
    if (!workingConfig) return
    setIsPublishing(true)
    await publishConfig(workingConfig)
    await revalidate()
    setIsPublishing(false)
  }, [workingConfig, publishConfig, revalidate])

  const patchConfig = useCallback(
    (updatedConfig: Partial<CmsWebsiteConfigBM>) => {
      setWorkingConfig((prev) =>
        prev ? { ...prev, ...updatedConfig } : undefined
      )
    },
    []
  )

  const handleFieldChange = useCallback(
    (fieldIndex: number, e: ChangeEvent<FileEntryBM | string | string[]>) => {
      if (
        !activeEntry ||
        !activeModel ||
        !workingConfig ||
        activeEntryIndex === null
      ) {
        return
      }

      const updatedEntities = { ...workingConfig.entities }
      updatedEntities[activeModel.key].entries[activeEntryIndex].fields[
        fieldIndex
      ].value = e.target.value

      patchConfig({ entities: updatedEntities })
    },
    [activeEntry, activeEntryIndex, activeModel, workingConfig, patchConfig]
  )

  const openEntry = useCallback(
    (model: CmsModel, entry: CmsModelEntry) => {
      setActiveModelUuid(model.uuid)
      const index =
        workingConfig?.entities[model.key].entries.findIndex(
          (e) => e.uuid === entry.uuid
        ) ?? -1
      if (index === -1) return
      setActiveEntryIndex(index)
    },
    [workingConfig]
  )

  return (
    <CmsEditorContext.Provider
      value={{
        editorData,
        actions: {
          saveConfig: saveConfig_,
          publishConfig: publishConfig_,
          patchConfig,
          openEntry,
          handleFieldChange,
        },
        status: {
          isSaved,
          isLoading,
          isError,
          isSaving,
          isPublishing,
          isPublished: !!backendResponse?.isPublished,
        },
      }}
    >
      {children}
    </CmsEditorContext.Provider>
  )
}
